import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Quote, Download, Share2, Loader2, ArrowRight } from "lucide-react";
import { toPng } from "html-to-image";
import api from "../../lib/api";

const hashSeed = (str) => {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

export const ProverbSpotlight = ({ seed = "" }) => {
  const [proverb, setProverb] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    api
      .get("/proverbs")
      .then((r) => {
        const list = r.data || [];
        if (!list.length) return;
        setProverb(list[hashSeed(seed) % list.length]);
      })
      .catch(() => {});
  }, [seed]);

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#12100e" });
  };

  const act = async (action) => {
    setBusy(action);
    try {
      const url = await generatePng();
      if (!url) return;
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = "yoruba-proverb.png";
        a.click();
      } else {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], "yoruba-proverb.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "A Yorùbá Proverb",
            text: `${proverb.yoruba} — via ÀsÀNigHT Worldwide`,
          });
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = "yoruba-proverb.png";
          a.click();
        }
      }
    } catch (e) {
      /* ignore (user cancelled, etc.) */
    } finally {
      setBusy("");
    }
  };

  if (!proverb) return null;

  return (
    <motion.aside
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="my-14 rounded-3xl bg-forest text-cream grain-overlay p-8 md:p-10 relative overflow-hidden"
      data-testid="proverb-spotlight"
    >
      <div className="absolute -top-6 -right-4 opacity-10 pointer-events-none">
        <Quote className="w-40 h-40 text-gold" />
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 mb-5">
          <Quote className="w-4 h-4 text-gold" />
          <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Proverb · Òwe</p>
          {proverb.theme && (
            <span className="text-[10px] uppercase tracking-widest border border-cream/25 rounded-full px-2.5 py-0.5 text-cream/60">
              {proverb.theme}
            </span>
          )}
        </div>

        <p className="font-serif text-2xl md:text-3xl text-gold leading-snug mb-4" data-testid="proverb-spotlight-yoruba">
          {proverb.yoruba}
        </p>
        {proverb.meaning && (
          <p className="text-cream/80 leading-relaxed mb-7 max-w-2xl">{proverb.meaning}</p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => act("download")}
            disabled={busy !== ""}
            data-testid="proverb-spotlight-download"
            className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download card
          </button>
          <button
            onClick={() => act("share")}
            disabled={busy !== ""}
            data-testid="proverb-spotlight-share"
            className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
          >
            {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Share
          </button>
          <Link
            to="/proverbs"
            data-testid="proverb-spotlight-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gold hover:text-gold-light transition-colors ml-auto"
          >
            More proverbs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Hidden branded share card (1080x1080) */}
      <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
        <div
          ref={cardRef}
          data-testid="proverb-spotlight-card"
          style={{
            width: 1080,
            height: 1080,
            background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
            color: "#F5EFE6",
            padding: 90,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

          <div style={{ position: "relative" }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>ÀsÀNigHT</div>
            <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>WORLDWIDE</div>
            <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>Yorùbá Proverb · Òwe</div>
          </div>

          <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
            <div style={{ fontSize: 120, color: "rgba(212,169,74,0.35)", fontFamily: "Georgia, serif", lineHeight: 0.6, marginBottom: 10 }}>“</div>
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                color: "#D4A94A",
                fontSize: proverb.yoruba.length > 120 ? 44 : proverb.yoruba.length > 70 ? 56 : 68,
                lineHeight: 1.15,
                marginBottom: 28,
              }}
            >
              {proverb.yoruba}
            </div>
            {proverb.meaning && (
              <div style={{ fontSize: 32, lineHeight: 1.4, color: "#F5EFE6" }}>{proverb.meaning}</div>
            )}
          </div>

          <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
            {proverb.theme && (
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>{proverb.theme}</div>
            )}
          </div>
        </div>
      </div>
    </motion.aside>
  );
};

export default ProverbSpotlight;
